import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--testing-tasks-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "deviceTypeSelect",
        "editModal",
        "editForm",
        "editNameInput",
        "editDeviceTypeSelect",
        "editIdInput"
    ]

    connect() {
        this.deviceTypeNames = {} // Cache for device type names
        this.initializeTable()
        this.loadDeviceTypes().then(() => {
            this.loadTestingTasks()
        })
        this._setupRowDoubleClick()
    }

    _setupRowDoubleClick() {
        if (!this.hasTableTarget) return
        this.tableTarget.addEventListener('dblclick', (e) => {
            const tr = e.target.closest('tbody tr')
            if (!tr) return
            const argusTable = this.application.getControllerForElementAndIdentifier(this.tableTarget, 'argus--table')
            if (!argusTable) return
            const rows = Array.from(this.tableTarget.querySelectorAll('tbody tr'))
            const index = rows.indexOf(tr)
            const data = argusTable.getData()
            const row = data[index]
            if (row && row.id) {
                window.location.href = `/settings/device-testing-tasks/${row.id}`
            }
        })
    }

    async loadDeviceTypes() {
        try {
            const response = await fetch('/api/device_types/device_type/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch device types')
            }

            const data = await response.json()
            this.deviceTypes = data.device_types || []
            
            // Build a lookup map of device type ID to name
            this.deviceTypeNames = {}
            this.deviceTypes.forEach(dt => {
                this.deviceTypeNames[dt.id] = dt.name
            })
            
            // Populate device type selects
            this.populateDeviceTypeSelects()
        } catch (error) {
            console.error('Error loading device types:', error)
        }
    }

    populateDeviceTypeSelects() {
        // Populate create modal device type select
        if (this.hasDeviceTypeSelectTarget) {
            this.populateSelect(this.deviceTypeSelectTarget, this.deviceTypes)
        }
        
        // Populate edit modal device type select
        if (this.hasEditDeviceTypeSelectTarget) {
            this.populateSelect(this.editDeviceTypeSelectTarget, this.deviceTypes)
        }
    }

    populateSelect(selectElement, deviceTypes) {
        // Clear existing options
        selectElement.innerHTML = '<option value="">Select a device type</option>'
        
        // Add device type options
        deviceTypes.forEach(dt => {
            const option = document.createElement('option')
            option.value = dt.id
            option.textContent = dt.name
            selectElement.appendChild(option)
        })
        
        // Reinitialize TomSelect if it exists
        if (selectElement.tomselect) {
            selectElement.tomselect.clearOptions()
            selectElement.tomselect.addOption(deviceTypes.map(dt => ({
                value: dt.id,
                text: dt.name
            })))
            selectElement.tomselect.refreshOptions(false)
        }
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadTestingTasks() {
        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch('/api/testing_tasks/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch testing tasks')
            }

            const data = await response.json()
            this.populateTable(data.testing_tasks || [])
        } catch (error) {
            console.error('Error loading testing tasks:', error)
            sendFailureNotification('Failed to load testing tasks')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(testingTasks) {
        if (this.hasTableTarget) {
            // Enrich testing tasks with device type names
            const enrichedTasks = testingTasks.map(task => ({
                ...task,
                device_type_name: this.deviceTypeNames[task.device_type] || task.device_type
            }))
            
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(enrichedTasks)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        this.showCreateModal()
    }

    showCreateModal() {
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        if (this.hasDeviceTypeSelectTarget) {
            this.deviceTypeSelectTarget.value = ''
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/testing_tasks/create', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to create testing task')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.createModalTarget)
            modal.hide()

            // Reload table
            await this.loadTestingTasks()

            sendSuccessNotification('Testing task created successfully')
        } catch (error) {
            console.error('Error creating testing task:', error)
            sendFailureNotification(error.message || 'Failed to create testing task')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const taskId = button.dataset.testingTaskId

        // Fetch testing task details
        this.fetchTestingTaskDetails(taskId)
    }

    async fetchTestingTaskDetails(taskId) {
        try {
            const response = await fetch(`/api/testing_tasks/describe?id=${taskId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch testing task details')
            }

            const result = await response.json()
            const testingTask = result.data

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = testingTask.name || ''
            }
            if (this.hasEditDeviceTypeSelectTarget) {
                if (this.editDeviceTypeSelectTarget.tomselect) {
                    this.editDeviceTypeSelectTarget.tomselect.setValue(testingTask.device_type || '')
                } else {
                    this.editDeviceTypeSelectTarget.value = testingTask.device_type || ''
                }
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = testingTask.id
            }

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching testing task details:', error)
            sendFailureNotification('Failed to load testing task details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/testing_tasks/update', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to update testing task')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.editModalTarget)
            modal.hide()

            // Reload table
            await this.loadTestingTasks()

            sendSuccessNotification('Testing task updated successfully')
        } catch (error) {
            console.error('Error updating testing task:', error)
            sendFailureNotification(error.message || 'Failed to update testing task')
        }
    }

    async deleteTestingTask(event) {
        event.preventDefault()

        const button = event.currentTarget
        const taskId = button.dataset.testingTaskId

        // Get task name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const taskName = nameCell ? nameCell.textContent : 'this testing task'

        if (!confirm(`Are you sure you want to delete ${taskName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', taskId)

        try {
            const response = await fetch('/api/testing_tasks/delete', {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to delete testing task')
            }

            // Reload table
            await this.loadTestingTasks()

            sendSuccessNotification('Testing task deleted successfully')
        } catch (error) {
            console.error('Error deleting testing task:', error)
            sendFailureNotification(error.message || 'Failed to delete testing task')
        }
    }

    viewStatuses(event) {
        event.preventDefault()

        const button = event.currentTarget
        const taskId = button.dataset.testingTaskId

        if (!taskId) {
            console.error('No task ID found in button dataset')
            sendFailureNotification('Unable to navigate: Task ID is missing')
            return
        }

        // Navigate to statuses page
        window.location.href = `/settings/device-testing-tasks/${taskId}`
    }
}
